import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Footer from '../components/Footer';
import IntelligenceSection from '../components/IntelligenceSection';
import RegionSelector from '../components/RegionSelector';
import { useRegion } from '../contexts/RegionContext';
import { getTitleIntelligence } from '../services/api';
import { trackEvent } from '../services/analytics';
import { setPageMetadata } from '../services/pageMetadata';
import { slugifyTitle } from '../services/titleLinks';
import { useIndustrySession } from '../services/industryAccess';

const TMDB_IMAGE = 'https://image.tmdb.org/t/p/';
const INDEXING_ENABLED = process.env.REACT_APP_CONTENT_INTELLIGENCE_INDEXING_ENABLED === 'true';

const Header = ({ industryAccess = false }) => (
  <header className="ci-nav">
    <Link to="/" className="ci-brand">What to Binge</Link>
    <nav>
      <Link to="/">Discover</Link>
      <Link to="/methodology">Methodology</Link>
      {industryAccess ? <Link to="/industry">Industry workspace</Link> : <a href="#industry-lens">For industry</a>}
    </nav>
  </header>
);

const MissingModule = ({ title, text }) => (
  <div className="ci-empty">
    <strong>{title}</strong>
    <p>{text}</p>
  </div>
);

const ContentIntelligencePage = () => {
  const { mediaType, tmdbId, slug } = useParams();
  const navigate = useNavigate();
  const { region } = useRegion();
  const [state, setState] = React.useState({ loading: true, data: null, error: null });
  const initialRegion = React.useRef(region);
  const viewedTitle = React.useRef(null);
  const industrySession = useIndustrySession();

  React.useEffect(() => {
    if (region !== initialRegion.current) {
      trackEvent('availability_territory_changed', { from: initialRegion.current, to: region });
      initialRegion.current = region;
    }
  }, [region]);

  React.useEffect(() => {
    let alive = true;
    setState({ loading: true, data: null, error: null });
    getTitleIntelligence(mediaType, tmdbId, region)
      .then((data) => {
        if (!alive) return;
        setState({ loading: false, data, error: null });
        if (viewedTitle.current !== data.title_ref.canonical_key) {
          viewedTitle.current = data.title_ref.canonical_key;
          trackEvent('title_intelligence_viewed', {
            title_key: data.title_ref.canonical_key,
            evidence_grade: data.evidence_coverage.grade,
            territory: region,
          });
        }
      })
      .catch((error) => {
        if (alive) setState({ loading: false, data: null, error });
      });
    return () => { alive = false; };
  }, [mediaType, tmdbId, region]);

  React.useEffect(() => {
    const data = state.data;
    if (!data) return;
    const canonicalSlug = slugifyTitle(data.metadata.title);
    if (slug !== canonicalSlug) {
      navigate(`/title/${data.title_ref.media_type}/${data.title_ref.tmdb_id}/${canonicalSlug}`, { replace: true });
      return;
    }
    const canonical = `https://what-to-binge.com/title/${data.title_ref.media_type}/${data.title_ref.tmdb_id}/${canonicalSlug}`;
    const description = `${data.summary.data?.text || data.metadata.overview} Explore content DNA, audience fit, availability and explained similar titles.`.slice(0, 200);
    setPageMetadata({
      title: `${data.metadata.title} — Content Intelligence | What to Binge`,
      description,
      canonical,
      image: data.metadata.backdrop_path ? `${TMDB_IMAGE}w1280${data.metadata.backdrop_path}` : undefined,
      type: data.title_ref.media_type === 'movie' ? 'video.movie' : 'video.tv_show',
      noindex: !INDEXING_ENABLED || data.evidence_coverage.grade === 'limited',
    });
  }, [state.data, slug, navigate]);

  const share = async () => {
    const title = state.data?.metadata.title || 'Title intelligence';
    const text = `Explore the content DNA, availability and closest explained neighbours of ${title}.`;
    try {
      if (navigator.share) await navigator.share({ title, text, url: window.location.href });
      else await navigator.clipboard.writeText(window.location.href);
      trackEvent('title_shared', { title_key: state.data?.title_ref.canonical_key, method: navigator.share ? 'native' : 'copy' });
    } catch (error) {
      if (error?.name !== 'AbortError') trackEvent('title_share_failed');
    }
  };

  if (state.loading) return (
    <main className="ci-page"><Header industryAccess={industrySession.allowed} /><div className="ci-loading" aria-live="polite"><span />Building the evidence view…</div></main>
  );

  if (state.error || !state.data) return (
    <main className="ci-page">
      <Header industryAccess={industrySession.allowed} />
      <div className="ci-error">
        <p className="ci-eyebrow">Intelligence unavailable</p>
        <h1>We cannot safely show this title yet.</h1>
        <p>The catalog may not contain this typed title, or a legacy movie/series ID collision would return the wrong record.</p>
        <Link to="/">Return to discovery</Link>
      </div>
    </main>
  );

  const d = state.data;
  const m = d.metadata;
  const runtime = m.runtime_minutes ? `${Math.floor(m.runtime_minutes / 60)}h ${m.runtime_minutes % 60}m` : null;
  const summary = d.summary.data?.text;
  const dna = d.content_dna.data || {};
  const audience = d.audience_dna.data?.archetypes || [];
  const audienceData = d.audience_dna.data || {};
  const similars = d.similar_titles.data?.results || [];
  const availability = d.availability.data || {};
  const regional = d.regional_relevance.data;
  const industry = d.industry_lens.data || {};

  return (
    <div className="ci-page">
      <Header industryAccess={industrySession.allowed} />
      <main>
        <section className="ci-hero">
          {m.backdrop_path && <img className="ci-hero__backdrop" src={`${TMDB_IMAGE}w1280${m.backdrop_path}`} alt="" />}
          <div className="ci-hero__veil" />
          <div className="ci-hero__content">
            {m.poster_path && <img className="ci-poster" src={`${TMDB_IMAGE}w342${m.poster_path}`} alt={`${m.title} poster`} />}
            <div className="ci-hero__copy">
              <div className="ci-hero__topline">
                <span>{d.title_ref.media_type === 'movie' ? 'Film intelligence' : 'Series intelligence'}</span>
                <RegionSelector />
              </div>
              <h1>{m.title}</h1>
              <p className="ci-meta">{[m.year, d.title_ref.media_type, runtime, m.seasons ? `${m.seasons} season${m.seasons === 1 ? '' : 's'}` : null, ...m.languages.slice(0, 2)].filter(Boolean).join(' · ')}</p>
              <p className="ci-summary">{summary}</p>
              <div className="ci-evidence-row">
                <span className={`ci-coverage ci-coverage--${d.evidence_coverage.grade}`}>{d.evidence_coverage.grade} evidence coverage</span>
                <span className="ci-momentum">Public interest: {d.public_interest.data?.trajectory || 'collecting history'}</span>
              </div>
              <div className="ci-actions">
                {m.trailer_key && <a href={`https://www.youtube.com/watch?v=${m.trailer_key}`} target="_blank" rel="noreferrer">Watch trailer</a>}
                <button onClick={share}>Share intelligence</button>
                <a href="#industry-lens" onClick={() => trackEvent('business_cta_clicked', { location: 'hero' })}>Industry lens</a>
              </div>
            </div>
          </div>
        </section>

        <div className="ci-body">
          <IntelligenceSection id="intelligence-overview" eyebrow="The brief" title="Intelligence Overview" module={d.why_it_matters}>
            {d.why_it_matters.data?.synopsis && <div className="ci-editorial-synopsis"><h3>Intelligence synopsis</h3><p>{d.why_it_matters.data.synopsis}</p></div>}
            <div className="ci-brief-grid">
              <article><span>What it offers</span><p>{d.why_it_matters.data?.what_it_offers}</p></article>
              <article><span>Audience promise</span><p>{d.why_it_matters.data?.audience_promise}</p></article>
              <article><span>Why relevant now</span><p>{d.why_it_matters.data?.why_relevant_now || 'No grounded catalyst signal is stored yet.'}</p></article>
            </div>
          </IntelligenceSection>

          <IntelligenceSection id="public-interest" eyebrow="Attention signals" title="Public interest" module={d.public_interest}>
            {d.public_interest.data?.snapshot ? <div className="ci-interest-snapshot"><article><span>TMDB popularity snapshot</span><strong>{d.public_interest.data.snapshot.tmdb_popularity ?? 'Not observed'}</strong></article><article><span>Wikipedia attention · latest 7 days</span><strong>{d.public_interest.data.wikimedia_momentum?.latest_7d_views?.toLocaleString?.() ?? 'Not available'}</strong>{d.public_interest.data.wikimedia_momentum?.change_7d_percent != null && <small>{d.public_interest.data.wikimedia_momentum.change_7d_percent > 0 ? '+' : ''}{d.public_interest.data.wikimedia_momentum.change_7d_percent}% versus prior 7 days</small>}</article><article><span>Trajectory</span><strong>{d.public_interest.data.trajectory || 'Collecting history'}</strong>{d.public_interest.data.wikimedia_momentum?.historical_weekly_percentile != null && <small>{d.public_interest.data.wikimedia_momentum.historical_weekly_percentile}th historical weekly percentile</small>}</article></div> : <MissingModule title="History starts with observation one" text="No historical points are stored, so this page does not draw a trend line or label momentum." />}
          </IntelligenceSection>

          <IntelligenceSection id="content-dna" eyebrow="Creative fingerprint" title="Content DNA" module={d.content_dna}>
            <div className="ci-dna-grid">
              {Object.keys(dna.dimensions || {}).length > 0 && <div><h3>Intensity profile</h3><div className="ci-chips">{Object.entries(dna.dimensions).map(([key, value]) => <span key={key}>{key.replaceAll('_', ' ')}<small> {String(value).replaceAll('_', ' ')}</small></span>)}</div></div>}
              {(dna.themes || []).length > 0 && <div><h3>Themes</h3><div className="ci-chips ci-chips--quiet">{dna.themes.map((x) => <span key={x}>{x.replaceAll('_', ' ')}</span>)}</div></div>}
              <div><h3>Inferred mood</h3><div className="ci-chips">{(dna.inferred_moods || []).map((x) => <span key={x.label} title={`Basis: ${x.basis.join(', ')}`}>{x.label}<small> inferred</small></span>)}</div></div>
              <div><h3>Observed descriptors</h3><div className="ci-chips ci-chips--quiet">{(dna.observed_descriptors || []).map((x) => <span key={x}>{x}</span>)}</div></div>
              <div><h3>Genres</h3><div className="ci-chips ci-chips--quiet">{(dna.observed_genres || []).map((x) => <span key={x}>{x}</span>)}</div></div>
            </div>
          </IntelligenceSection>

          <IntelligenceSection id="audience-dna" eyebrow="Affinity, not demographics" title="Audience DNA" module={d.audience_dna}>
            {audience.length ? <><div className="ci-audience-grid">{audience.map((item) => <article key={item.label}><h3>{item.label}</h3><p>Inferred from: {item.basis.join(', ')}</p></article>)}</div>{(audienceData.motivations || []).length > 0 && <div className="ci-chips ci-chips--quiet">{audienceData.motivations.map((x) => <span key={x}>{x.replaceAll('_', ' ')}</span>)}</div>}<p className="ci-observation">{[audienceData.commitment && `${audienceData.commitment} commitment`, audienceData.social_context && audienceData.social_context.replaceAll('_', ' ')].filter(Boolean).join(' · ')}</p></> : <MissingModule title="Audience archetypes unavailable" text="The current title descriptors do not map safely to the controlled audience taxonomy." />}
            <p className="ci-observation"><strong>Why no demographics?</strong> We do not currently have a licensed audience panel or consented first-party dataset measuring age, gender, income, or household composition. The signals above describe content affinity inferred from title evidence—not measured viewer demographics.</p>
          </IntelligenceSection>

          <IntelligenceSection id="similar-titles" eyebrow="Explained neighbours" title="Similar—along specific dimensions" module={d.similar_titles}>
            {similars.length ? <div className="ci-similar-grid">{similars.map((item) => (
              <Link key={item.title_ref.canonical_key} to={`/title/${item.title_ref.media_type}/${item.title_ref.tmdb_id}/${slugifyTitle(item.title)}`} onClick={() => trackEvent('similar_title_clicked', { source: d.title_ref.canonical_key, target: item.title_ref.canonical_key })}>
                {item.poster_path && <img src={`${TMDB_IMAGE}w342${item.poster_path}`} alt="" loading="lazy" />}
                <div><span>{item.similarity_band} match</span><h3>{item.title}{item.year ? ` (${item.year})` : ''}</h3><p>{item.why_similar}</p>{item.meaningful_differences?.[0] && <small>Different: {item.meaningful_differences[0]}</small>}</div>
              </Link>
            ))}</div> : <MissingModule title="No type-safe neighbours available" text="We do not substitute generic poster recommendations when the graph cannot support an explanation." />}
          </IntelligenceSection>

          <IntelligenceSection id="availability" eyebrow="Source-aware availability" title={`Observed availability · ${(availability.territory || region) === 'WW' ? 'Worldwide summary' : (availability.territory || region)}`} module={d.availability}>
            {availability.offers?.length ? <div className="ci-provider-grid">{availability.offers.map((offer, i) => <div key={`${offer.provider_name}-${offer.offer_type}-${i}`}>{offer.logo_path && <img src={`${TMDB_IMAGE}w92${offer.logo_path}`} alt="" />}<span><strong>{offer.provider_name}</strong><small>{offer.offer_type}{offer.territories?.length > 1 ? ` · ${offer.territories.length} territories` : ''}</small></span></div>)}</div> : <MissingModule title="Not observed in this source" text={`No provider offer is present for ${region} in the current TMDB catalog snapshot.`} />}
            <p className="ci-observation">{availability.source_checked_at ? `Observed by What to Binge ${new Date(availability.source_checked_at).toLocaleDateString()}` : 'Provider observation time not retained in the legacy snapshot'} · Observed across {availability.observed_territory_count || 0} territories</p>
          </IntelligenceSection>

          <IntelligenceSection id="regional-relevance" eyebrow="Evidence by territory" title="Regional relevance" module={d.regional_relevance}>
            {regional?.basis?.length ? <div className="ci-basis-list">{regional.basis.map((b, i) => <p key={i}><strong>{b.signal.replaceAll('_', ' ')}</strong>{b.observation}</p>)}</div> : <MissingModule title="Regional evidence is limited" text="Availability alone cannot support a demand or cultural-relevance claim." />}
          </IntelligenceSection>

          <IntelligenceSection id="industry-lens" eyebrow={industrySession.allowed ? 'Internal industry preview' : 'Public business preview'} title="Industry Lens" module={d.industry_lens} distinct>
            <div className="ci-industry-grid">
              <article><span>Availability density</span><strong>{industry.observed_availability_density?.interpretation}</strong><p>{industry.observed_availability_density?.territories || 0} observed territories</p></article>
              <article><span>Audience promise</span><strong>{industry.audience_promise?.join(' · ') || 'Limited evidence'}</strong><p>Content affinity, not demographic measurement</p></article>
              <article><span>Platform fit</span><strong>Needs your context</strong><p>No universal acquisition score is shown.</p></article>
            </div>
            {industrySession.allowed ? <div className="ci-industry-cta"><div><h3>Current Industry Lens scope</h3><p>This preview contains neutral title intelligence. Platform-specific strategic fit, acquisition recommendations, rights economics, and measured audience demographics require company and licensed market data and are not calculated yet.</p></div><Link to="/industry" onClick={() => trackEvent('industry_workspace_opened', { location: 'industry_lens' })}>Open industry workspace</Link></div> : industrySession.loading ? <div className="ci-industry-cta"><div><h3>Checking industry access…</h3><p>Your signed-in entitlement is being verified.</p></div></div> : <div className="ci-industry-cta"><div><h3>See how this title fits your platform</h3><p>Join the BingeIQ beta for territory- and strategy-specific intelligence as it becomes available.</p></div><Link to={`/bingeiq-beta?title=${encodeURIComponent(d.title_ref.canonical_key)}`} onClick={() => trackEvent('business_cta_clicked', { location: 'industry_lens' })}>Join the BingeIQ beta</Link></div>}
          </IntelligenceSection>

          <div className="ci-coverage-note"><strong>Evidence coverage: {d.evidence_coverage.grade}</strong><p>{d.evidence_coverage.note}</p><span>Available: {d.evidence_coverage.available_dimensions.join(', ')}.</span><span>Missing: {d.evidence_coverage.missing_dimensions.join(', ')}.</span></div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ContentIntelligencePage;
